"use client";

import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

// Validation schema
const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const verified = searchParams.get("verified");
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslations("auth.login");
  const tValidation = useTranslations("auth.validation");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError("");

    try {
      const result = await signIn("credentials", {
        email: data.email,
        password: data.password,
        redirect: false,
      });

      if (result?.error) {
        setError(t("invalidCredentials"));
        return;
      }

      if (result?.ok) {
        router.push("/dashboard");
        router.refresh();
      }
    } catch (err) {
      setError(t("unexpectedError"));
      console.error("Login error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("title")}</CardTitle>
        <CardDescription>
          {t("description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Verified Success Alert */}
          {verified === "true" && (
            <div className="flex items-center gap-2 rounded-md bg-success/10 p-3 text-sm text-success dark:bg-green-900/20 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              <span>{t("emailVerified")}</span>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <div className="flex items-center gap-2 rounded-md bg-error/10 p-3 text-sm text-error dark:bg-red-900/20 dark:text-error">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}

          {/* Email Field */}
          <div className="space-y-2">
            <Input
              {...register("email")}
              type="email"
              label={t("emailLabel")}
              placeholder={t("emailPlaceholder")}
              error={errors.email?.message}
              disabled={isLoading}
              autoComplete="email"
              autoFocus
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <Input
              {...register("password")}
              type="password"
              label={t("passwordLabel")}
              placeholder={t("passwordPlaceholder")}
              error={errors.password?.message}
              disabled={isLoading}
              autoComplete="current-password"
            />
            <div className="text-right">
              <Link
                href="/forgot-password"
                className="text-sm font-medium text-[var(--color-primary,#3b82f6)] hover:underline"
              >
                {t("forgotPassword")}
              </Link>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("submitting")}
              </>
            ) : (
              t("submitButton")
            )}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col space-y-4">
        <div className="text-center text-sm text-gray-600 dark:text-gray-400">
          {t("noAccount")}{" "}
          <Link
            href="/signup"
            className="font-medium text-[var(--color-primary,#3b82f6)] hover:underline"
          >
            {t("signUp")}
          </Link>
        </div>
      </CardFooter>
    </Card>
  );
}

function LoadingCard() {
  const t = useTranslations("auth.login");
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("title")}</CardTitle>
        <CardDescription>{t("loading")}</CardDescription>
      </CardHeader>
      <CardContent className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </CardContent>
    </Card>
  );
}

export default function LoginPage() {
  const t = useTranslations("auth");

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 dark:bg-gray-900 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        {/* Logo/Brand */}
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            JobWright
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t("brandTagline")}
          </p>
        </div>

        <Suspense fallback={<LoadingCard />}>
          <LoginForm />
        </Suspense>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 dark:text-gray-400">
          {t("termsAgreement")}
        </p>
      </div>
    </div>
  );
}
